import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom'
import { OneRosterCombineTeamListing, CoreOneRosterConfigurationCombinedTeam, CoreOneRosterConfigurationCombinedTeamSource } from "../../../../../../models/CoreOneRosterConfigurationCombinedTeam";
import { RequestState } from "../../../../../../models/RequestState";
import { GetOneRosterCombineTeamGroups, UpdateOneRosterCombineTeam } from "../../../../../../store/OneRoster/OneRosterCombineTeamAction";
import { FormGroup, Label, Input } from 'reactstrap';
import MultiSelectInput from '../../../../../misc/MultiSelectInput';
import StatusCode from '../../../../../../util/StatusCode';
import { SearchableOptionItem } from '../../../../../../util/UtilityTypes';
import ModalComponent from "../../../../../misc/ModalComponent";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../../../store/store";


interface OneRosterCombineTeamGroupEditProps {
    isOpen: boolean
    editTeam: CoreOneRosterConfigurationCombinedTeam
    toggleModal: () => void
    success: (success: boolean) => void
    disabled: boolean
}

const validation = (team: CoreOneRosterConfigurationCombinedTeam) => {
    let valid: string[] = [];
    if (team.displayName === "") {
        valid.push('displayName')
    }
    if (team.courseTitle === "") {
        valid.push('courseTitle')
    }
    if (!team.sources.length) {
        valid.push('sources')
    }
    return valid;
}

const OneRosterCombineTeamGroupsEdit: React.FC<OneRosterCombineTeamGroupEditProps> = (props) => {
    const { moduleId } = useParams();
    const dispatch = useDispatch();
    const [selectedGroups, setSelectedGroups] = useState<string[] | undefined>();
    const [team, setTeam] = useState<CoreOneRosterConfigurationCombinedTeam>(props.editTeam)
    const groups = useSelector<RootState, RequestState<OneRosterCombineTeamListing[]>>(state => state.onerosterCombineTeam.groups);
    const [savedGroups, setSavedGroups] = useState<OneRosterCombineTeamListing[]>([]);

    const [status, setStatus] = useState<StatusCode>(StatusCode.NONE);
    const { t } = useTranslation(['configuration', 'common']);
    const valid = validation(team);


    const updateSelectedGroups = () => {
        let sourceGroups: OneRosterCombineTeamListing[] = [];
        team.sources.map(x => {
            if (x.flowSyncGroupId !== undefined && x.flowSyncGroupTitle !== undefined) {
                sourceGroups.push({ id: x.flowSyncGroupId, title: x.flowSyncGroupTitle, schoolUnitTitle: x.schoolUnitTitle })
            }
        });

        setSavedGroups(sourceGroups);
        setSelectedGroups(sourceGroups.map(x => x.id));
    }


    useEffect(() => {
        if (moduleId) {
            GetOneRosterCombineTeamGroups(moduleId)(dispatch);
            updateSelectedGroups();
        }
    }, [dispatch, moduleId]);

    const updateCombinedTeam = () => {
        if (!valid.length && moduleId) {
            setStatus(StatusCode.PENDING);
            UpdateOneRosterCombineTeam(moduleId, team)
                .then(x => {
                    setStatus(StatusCode.COMPLETE)
                    props.success(true)
                })
                .catch(error => {
                    setStatus(StatusCode.ERROR)
                })
        }
    }

    const updateGroup = (name: string, value: string[] | null | undefined) => {
        if (value === null) {
            setSelectedGroups(undefined);
            return;
        }
        setSelectedGroups(value)

        if (value !== undefined) {
            let sources: CoreOneRosterConfigurationCombinedTeamSource[] = [];
            value.map(x => {
                sources.push({ "flowSyncGroupId": x })
            })
            setTeam({ ...team, "sources": sources });
        }
    };

    const updateTeam = (name: string, value: undefined | string) => {
        setTeam({ ...team, [name]: value });
    };

    const mapGroups = () => {
        let groupOptions: SearchableOptionItem<string>[] = [];
        if (groups.code === StatusCode.COMPLETE) {
            let groupListings = groups.data;
            if (savedGroups.length > 0) {
                groupListings = [...groupListings, ...savedGroups]
            }

            groupOptions = groupListings
                .filter(x => savedGroups.some(y => y.id === x.id || x.id !== team.flowSyncGroupId))
                .map(x => ({ value: x.id, label: `${x.title}, ${x.schoolUnitTitle}` }))
        }
        return groupOptions;
    };

    return (<>
        <ModalComponent
            isOpen={props.isOpen}
            toggleModal={() => props.toggleModal()}
            header={t('oneRosterConfiguration.combineTeam.title', { ns: 'configuration' })}
            update={updateCombinedTeam}
            disabled={valid.length > 0 || props.disabled}
            confirm={t('action.edit', { ns: 'common' })}
            cancel={t('action.cancel', { ns: 'common' })}
            saveStatus={status}
        >

            <FormGroup>
                <Label for="displayName">{t('oneRosterConfiguration.combineTeam.table.displayName', { ns: 'configuration' })}</Label>
                <Input
                    type="text"
                    name="displayName"
                    id="displayName"
                    autoComplete="off"
                    value={team.displayName}
                    onChange={(e) => updateTeam("displayName", e.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label for="courseTitle">{t('oneRosterConfiguration.combineTeam.table.courseTitle', { ns: 'configuration' })}</Label>
                <Input
                    type="text"
                    name="courseTitle"
                    id="courseTitle"
                    autoComplete="off"
                    value={team.courseTitle}
                    onChange={(e) => updateTeam("courseTitle", e.target.value)}
                />
            </FormGroup>

            <Label>{t('oneRosterConfiguration.combineTeam.table.owner', { ns: 'configuration' })}</Label>
            <p>{team.flowSyncGroupTitle}, {team.schoolUnitTitle}</p>

            <FormGroup>
                <Label>{t('oneRosterConfiguration.combineTeam.table.sources', { ns: 'configuration' })}</Label>
                <MultiSelectInput
                    items={mapGroups()}
                    onChange={(name, value) => updateGroup(name, value)}
                    name='sources'
                    selected={selectedGroups}
                    defaultText={t('oneRosterConfiguration.combineTeam.select.sources', { ns: 'configuration' })}
                    disabled={groups.code !== StatusCode.COMPLETE || team?.flowSyncGroupId === undefined}
                />
            </FormGroup>
        </ModalComponent>
    </>)
}

export default OneRosterCombineTeamGroupsEdit;
